import React, { useState } from "react";
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaPaperPlane } from "react-icons/fa";
import { GiHearts } from "react-icons/gi";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    type: "query", // 'query' or 'feedback'
    message: ""
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const { name, email, message } = formData;
    const errors = {};
    
    if (!name.trim()) errors.name = "Name is required";
    if (!email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Email is invalid";
    }
    if (!message.trim()) errors.message = "Message is required";
    
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm();
    
    if (Object.keys(errors).length === 0) {
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: "",
          email: "",
          type: "query",
          message: ""
        });
      }, 3000);
    } else {
      setFormErrors(errors);
    }
  };

  return (
    <section className="py-14 px-4 bg-gradient-to-t from-pink-50 to-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center mb-4">
            <GiHearts className="text-pink-400 text-4xl mr-2" />
            <h2 className="text-4xl font-cormorant font-bold text-pink-600">
              Contact Us
            </h2>
            <GiHearts className="text-pink-400 text-4xl ml-2" />
          </div>
          <p className="text-lg text-pink-700 max-w-2xl mx-auto">
            We're here to support your wellness journey. Reach out with any questions or just to say hello!
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Side - Contact Info */}
          <div className="w-full lg:w-1/2 bg-white p-8 rounded-2xl shadow-lg border border-pink-100">
            <div className="flex items-center mb-6">
              <div className="bg-pink-100 p-3 rounded-full mr-4">
                <FaMapMarkerAlt className="text-pink-600 text-xl" />
              </div>
              <div>
                <h3 className="text-2xl font-cormorant font-bold text-pink-600">Our Location</h3>
                <p className="text-gray-600 mt-1">
                  Fruit St, Boston, <br /> MA 02114, United States
                </p>
              </div>
            </div>

            <div className="flex items-center mb-6">
              <div className="bg-pink-100 p-3 rounded-full mr-4">
                <FaEnvelope className="text-pink-600 text-xl" />
              </div>
              <div>
                <h3 className="text-2xl font-cormorant font-bold text-pink-600">Email Us</h3>
                <p className="text-gray-600 mt-1">
                  <a href="mailto:flowheartshelp@gmail.com">flowheartshelp@gmail.com</a>
                </p>
              </div>
            </div>

            <div className="mt-10">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d219148.7807483307!2d-70.92003402074589!3d42.11140069604207!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e37099b9bb2b5d%3A0xaf4cb175c9469624!2sFruit%20St%2C%20Boston%2C%20MA%2002114%2C%20USA!5e1!3m2!1sen!2sin!4v1747377449962!5m2!1sen!2sin" 
                width="100%" 
                height="500" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* Right Side - Contact Form */}
          <div className="w-full lg:w-1/2 bg-white p-8 rounded-2xl shadow-lg border border-pink-100">
            <div className="mb-8 text-center">
              <h3 className="text-3xl font-cormorant font-bold text-pink-600 mb-2">
                Send Us a Message
              </h3>
              <p className="text-pink-500">
                We typically respond within 24 hours
              </p>
            </div>
            
            {isSubmitted ? (
              <div className="bg-pink-100 text-pink-700 p-4 rounded-lg text-center">
                <p className="font-medium">Thank you for your message!</p>
                <p className="text-sm">We'll get back to you soon ❤️</p>
              </div>
            ) : (
              <form 
                action="https://formspree.io/f/xnndwjez" 
                method="POST"
                onSubmit={() => setIsSubmitted(true)}
                className="space-y-6"
              >
                <div>
                  <label htmlFor="name" className="block text-pink-700 mb-2 font-medium">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="w-full px-4 py-3 border-2 border-pink-100 rounded-xl focus:outline-none focus:border-pink-300"
                    placeholder="Enter your name"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-pink-700 mb-2 font-medium">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="w-full px-4 py-3 border-2 border-pink-100 rounded-xl focus:outline-none focus:border-pink-300"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="type" className="block text-pink-700 mb-2 font-medium">
                    I'd like to...
                  </label>
                  <select
                    id="type"
                    name="type"
                    className="w-full px-4 py-3 border-2 border-pink-100 rounded-xl focus:outline-none focus:border-pink-300 appearance-none bg-white bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiNlMTFkNDgiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBjbGFzcz0ibHVjaWRlIGx1Y2lkZS1jaGV2cm9uLWRvd24iPjxwYXRoIGQ9Im03IDE1IDUgNSA1LTUiLz48L3N2Zz4=')] bg-no-repeat bg-[right_1rem_center]"
                  >
                    <option value="query">Ask a question</option>
                    <option value="feedback">Share feedback</option>
                    <option value="support">Get support</option>
                    <option value="other">Something else</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-pink-700 mb-2 font-medium">
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    className="w-full px-4 py-3 border-2 border-pink-100 rounded-xl focus:outline-none focus:border-pink-300"
                    placeholder="Tell us how we can help..."
                    required
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-xl hover:from-pink-600 hover:to-pink-700 transition-all font-medium text-lg shadow-lg hover:shadow-pink-200"
                >
                  <FaPaperPlane className="animate-bounce" /> 
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;